import { useEffect } from "react";
import PickerAccessDeniedPopup from "./PickerAccessDeniedPopup";
import useInboxQueryParams from "hooks/useInboxQueryParams";
import { PICKER_BASE_EDIT_LINK, PICKER_BASE_LINK } from "./helpers";
import { fetchIsPickerUser } from "components/Pages/Home/UploaderComponent/PickerPromo/api";
import { usePicker } from "contexts/pickerContext/pickerContext";
import { useContent } from "contexts/contentContext/contentContext";
import { useUser } from "contexts/userContext/userContext";
import { useSearchParams } from "react-router-dom";
import { getPickerProjectIdFromURL } from "../helpers";
import { UserDataT } from "models/models";
import { useModal } from "contexts/modalContext/modalContext";
import PickerPromoFreeModal from "components/Pages/Home/UploaderComponent/PickerPromo/Modals/PickerPromoFreeModal";

const PRO_PICKER_PROJECTS_COUNT = 1;
const BUSINESS_PICKER_PROJECTS_COUNT = 15;

type PickerActionT = "create" | "convert" | "edit" | "view";

const usePickerQueryParamActions = () => {
  const {
    getIFrameLink,
    setIFrameLink,
    fetchPickerMailList,
    fetchPickerProjectById,
    getPickerMailList,
  } = usePicker();
  const { getLanguage } = useContent();
  const {
    getUserData,
    isPro,
    isPremium,
    isBusiness,
    isSubscribed,
    premiumExpired,
  } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const { removeQueryParam, setQueryParams } = useInboxQueryParams();
  const { setModal } = useModal();

  // handle navigation in browser history
  useEffect(() => {
    if (
      !searchParams.get("create") &&
      !searchParams.get("convert") &&
      !searchParams.get("edit")
    ) {
      setIFrameLink("");
    }
  }, [
    searchParams.get("create"),
    searchParams.get("convert"),
    searchParams.get("edit"),
  ]);

  const initializeIframe = () => {
    if (searchParams.get("create")) {
      setIFrameLink(getCreateLink());
    }
    if (searchParams.get("convert")) {
      removeQueryParam("convert");
    }
    if (searchParams.get("edit") && searchParams.get("subdomain")) {
      setIFrameLink(
        getEditLink(searchParams.get("subdomain")!, searchParams.get("edit")!)
      );
    }
  };

  // iframe links ------------------------------------------------- START -------------------------------------------------

  const getCreateLink = () =>
    `${PICKER_BASE_EDIT_LINK}cussite/edit.php?userid=${
      getUserData()?.UserEncryptedId
    }&create&lang=${getLanguage()}`;

  const getConvertLink = (mailId: string) =>
    `${PICKER_BASE_EDIT_LINK}cussite/?p=info&te=201&jm_uploaded&sid=${mailId}&lang=${getLanguage()}`;

  const getViewLink = (subdomain: string, galleryId: string) =>
    `${PICKER_BASE_LINK}${subdomain}/pr/?pixelect&idc=${galleryId}&lang=${getLanguage()}`;

  const getEditLink = (subdomain: string, galleryId: string) =>
    `${PICKER_BASE_EDIT_LINK}cussite/edit.php?cid=${subdomain}&edit&idc=${galleryId}&lang=${getLanguage()}`;

  // iframe links ------------------------------------------------- END ---------------------------------------------------

  const openIframe = async (
    type: PickerActionT,
    linkData?: {
      mailId?: string;
      subdomain?: string;
      galleryId?: string;
    }
  ) => {
    if (type === "convert" || type === "create") {
      // 2996
      // const isPickerUser = !isSubscribed()
      //   ? await fetchIsPickerUser(getUserData()?.UserEncryptedId)
      //   : null;

      const hasAccess = checkPickerAccess(
        getPickerMailList()?.length,
        type,
        isSubscribed(),
        isPro(),
        isBusiness(),
        isPremium(),
        premiumExpired(),
        setModal
        // isPickerUser
      );

      if (!hasAccess) {
        return;
      }
    }

    switch (type) {
      case "create": {
        setIFrameLink(getCreateLink());
        setSearchParams({
          t: "picker",
          create: "true",
        });
        break;
      }
      case "convert": {
        setIFrameLink(getConvertLink(linkData?.mailId!));
        setSearchParams({
          t: "picker",
          convert: "true",
        });
        break;
      }
      case "edit": {
        setIFrameLink(getEditLink(linkData?.subdomain!, linkData?.galleryId!));
        setSearchParams({
          t: "picker",
          edit: linkData?.galleryId!,
          subdomain: linkData?.subdomain!,
        });
        break;
      }
      case "view": {
        window.open(
          getViewLink(linkData?.subdomain!, linkData?.galleryId!),
          "_blank"
        );
        break;
      }
    }
  };

  const closeIframe = (cb?: () => void) => {
    if (searchParams.get("edit")) {
      fetchPickerProjectById(
        getPickerProjectIdFromURL(getIFrameLink()),
        getUserData()?.UserEncryptedId
      );
    }
    if (searchParams.get("t") === "picker") {
      setSearchParams({ t: "picker" });
    } else {
      setQueryParams({ t: "picker" });
    }

    if (searchParams.get("create") || searchParams.get("convert")) {
      fetchPickerMailList((getUserData() as UserDataT)?.UserEncryptedId);
    }

    // close iframe
    setIFrameLink("");

    // clear query params
    removeQueryParam(["create", "convert", "edit", "subdomain"]);

    cb?.();
  };

  return {
    openIframe,
    closeIframe,
    getEditLink,
    getViewLink,
    getConvertLink,
    getCreateLink,
    initializeIframe,
  };
};

const checkPickerAccess = (
  projectsCount: number,
  type: PickerActionT,
  isSubscribed: boolean,
  isPro: boolean,
  isBusiness: boolean,
  isPremium: boolean,
  premiumExpired: boolean,
  setModal: (params: any) => void
  // isPickerUser: boolean | null
) => {
  // if (
  //   (!isSubscribed && (isPickerUser || isPickerUser === null)) ||
  //   (isPremium && premiumExpired)
  // ) {
  if (!isSubscribed || (isPremium && premiumExpired)) {
    setModal({
      modal: PickerPromoFreeModal,
    });
    return false;
  }

  if (
    ((type === "create" || type === "convert") &&
      isPro &&
      projectsCount >= PRO_PICKER_PROJECTS_COUNT) ||
    (isBusiness && projectsCount >= BUSINESS_PICKER_PROJECTS_COUNT)
  ) {
    setModal({
      modal: PickerPromoFreeModal,
    });
    return false;
  }

  return true;
};

export default usePickerQueryParamActions;
